import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Field, getFormValues, reduxForm } from 'redux-form';
import { Button } from 'reactstrap';
import { connect } from 'react-redux';
import renderField from '../../../shared/components/custom/Field';
import FormModal from '../../../shared/components/custom/FormModal';
import InfoBox from '../../../shared/components/custom/InfoBox';
import SwitchRow from '../../../shared/components/custom/SwitchRow';
import { partial } from '../../../util/translation/translation';
import {
  FEE_FIXED_MAX_KR,
  FEE_PERCENT_MAX,
  PRODUCT_TYPES,
  feeOn100Kr,
  formatKr,
  hasMaxTwoDecimals,
  krToOre,
  oreToKr,
  parseDecimal,
} from '../connect';

const FORM = 'connect_fee_form';

// A fee's two fields (by their names) checked; errors added to `errors`
const validateFee = (values, percentName, fixedName, errors) => {
  const p = partial('connect');
  const percent = parseDecimal(values[percentName]);
  if (
    !hasMaxTwoDecimals(values[percentName]) ||
    percent < 0 ||
    percent > FEE_PERCENT_MAX
  ) {
    errors[percentName] = p('feePercentInvalid', { max: FEE_PERCENT_MAX });
  }
  const fixed = parseDecimal(values[fixedName]);
  if (
    !hasMaxTwoDecimals(values[fixedName]) ||
    fixed < 0 ||
    fixed > FEE_FIXED_MAX_KR
  ) {
    errors[fixedName] = p('feeFixedInvalid', { max: FEE_FIXED_MAX_KR });
  }
};

const validate = (values) => {
  const errors = {};
  validateFee(values, 'feePercent', 'feeFixedKr', errors);
  PRODUCT_TYPES.forEach((type) => {
    if (values[`${type}_own`]) {
      validateFee(values, `${type}_percent`, `${type}_fixedKr`, errors);
    }
  });
  return errors;
};

/** The form's fees as the transactions service takes them. */
export const feesFromValues = (values) => ({
  fee: {
    feePercent: parseDecimal(values.feePercent),
    feeFixedAmount: krToOre(values.feeFixedKr),
  },
  // A kind's own fee, or null for the standard one
  productFees: Object.fromEntries(
    PRODUCT_TYPES.map((type) => [
      type,
      values[`${type}_own`]
        ? {
            feePercent: parseDecimal(values[`${type}_percent`]),
            feeFixedAmount: krToOre(values[`${type}_fixedKr`]),
          }
        : null,
    ])
  ),
});

// A percentage and a fixed amount, with what they come to on 100 kr: the
// club's part and the platform's share (Stripe's fee is paid out of it)
const FeeFields = ({ percentName, fixedName, values, errors }) => {
  const p = partial('connect');
  const valid = !errors[percentName] && !errors[fixedName];
  const share = valid
    ? feeOn100Kr(parseDecimal(values[percentName]), krToOre(values[fixedName]))
    : null;
  return (
    <>
      <div className="connect-fee__fields">
        <div className="form__form-group">
          <span className="form-header">{p('feePercent')}</span>
          <div className="form__form-group-field">
            <Field
              name={percentName}
              component={renderField}
              inputMode="decimal"
              placeholder="6.5"
            />
            <div className="form__form-group-icon">&nbsp;%&nbsp;</div>
          </div>
        </div>
        <div className="form__form-group">
          <span className="form-header">{p('feeFixed')}</span>
          <div className="form__form-group-field">
            <Field
              name={fixedName}
              component={renderField}
              inputMode="decimal"
              placeholder="1.80"
            />
            <div className="form__form-group-icon">&nbsp;{p('kr')}&nbsp;</div>
          </div>
        </div>
      </div>
      <p className="connect-fee__example">
        {share === null
          ? p('feeExampleEmpty')
          : p('feeExample', {
              club: formatKr(10000 - share),
              amount: formatKr(share),
            })}
      </p>
    </>
  );
};

FeeFields.propTypes = {
  percentName: PropTypes.string.isRequired,
  fixedName: PropTypes.string.isRequired,
  values: PropTypes.shape({}).isRequired,
  errors: PropTypes.shape({}).isRequired,
};

// The platform's share of a receiver's payments: a standard fee, and its own
// fee for some kinds of product (auctions, tiers...). Each is a percentage
// plus a fixed amount per payment, typed in kronor and saved in øre. Stripe's
// fee is included: it's paid out of the platform's share (destination
// charges), never added on top for the club.
const ConnectFeeModal = ({
  receiver,
  clubName,
  onClose,
  handleSubmit,
  initialize,
  formValues,
  submitting,
}) => {
  const p = partial('connect');
  useEffect(() => {
    if (receiver) {
      const productFields = PRODUCT_TYPES.flatMap((type) => {
        const own = receiver.productFees?.[type];
        return [
          [`${type}_own`, !!own],
          [`${type}_percent`, own ? `${own.feePercent}` : ''],
          [`${type}_fixedKr`, own ? oreToKr(own.feeFixedAmount) || '0' : '0'],
        ];
      });
      initialize({
        feePercent:
          receiver.feePercent === null || receiver.feePercent === undefined
            ? ''
            : `${receiver.feePercent}`,
        feeFixedKr: oreToKr(receiver.feeFixedAmount) || '0',
        ...Object.fromEntries(productFields),
      });
    }
    // initialize is a stable redux-form dispatch
    // eslint-disable-next-line
  }, [receiver]);
  const values = formValues || {};
  const errors = validate(values);

  return (
    <FormModal
      isOpen={!!receiver}
      onClose={onClose}
      title={p('setFee')}
      subtitle={receiver ? `${receiver.name} · ${clubName}` : null}
    >
      <form className="form" onSubmit={handleSubmit}>
        <div className="qm-body">
          <InfoBox className="mb-3">
            <strong>{p('feeStripeIncludedTitle')}</strong>{' '}
            {p('feeStripeIncluded')}
          </InfoBox>

          <h5 className="connect-fee__heading">{p('feeStandard')}</h5>
          <p className="connect-fee__help">{p('feeStandardHelp')}</p>
          <FeeFields
            percentName="feePercent"
            fixedName="feeFixedKr"
            values={values}
            errors={errors}
          />

          <h5 className="connect-fee__heading">{p('productFees')}</h5>
          <p className="connect-fee__help">{p('productFeesHelp')}</p>
          {PRODUCT_TYPES.map((type) => (
            <div className="connect-fee__product" key={type}>
              <Field
                name={`${type}_own`}
                component={SwitchRow}
                label={p(`product-${type}`)}
              />
              {values[`${type}_own`] ? (
                <FeeFields
                  percentName={`${type}_percent`}
                  fixedName={`${type}_fixedKr`}
                  values={values}
                  errors={errors}
                />
              ) : (
                <p className="connect-fee__help">{p('productUsesStandard')}</p>
              )}
            </div>
          ))}
        </div>
        <div className="qm-footer">
          <Button type="button" outline onClick={onClose}>
            {p('cancel')}
          </Button>
          <Button
            type="submit"
            color="primary"
            disabled={submitting || Object.keys(errors).length > 0}
          >
            {p('saveFee')}
          </Button>
        </div>
      </form>
    </FormModal>
  );
};

const feeShape = PropTypes.shape({
  feePercent: PropTypes.number,
  feeFixedAmount: PropTypes.number,
});

ConnectFeeModal.propTypes = {
  receiver: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
    feePercent: PropTypes.number,
    feeFixedAmount: PropTypes.number,
    productFees: PropTypes.objectOf(feeShape),
  }),
  clubName: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  initialize: PropTypes.func.isRequired,
  formValues: PropTypes.shape({}),
  submitting: PropTypes.bool,
};

ConnectFeeModal.defaultProps = {
  receiver: null,
  clubName: '',
  formValues: null,
  submitting: false,
};

export default connect((state) => ({
  formValues: getFormValues(FORM)(state),
}))(reduxForm({ form: FORM, validate })(ConnectFeeModal));
